import { atom } from "nanostores";
import { io, Socket } from "socket.io-client";

declare global {
  interface Window {
    dataLayer: any[];
  }
}
import axios from "axios";
import { type ChatMessage, type Language } from "../types/globalTypes";
import { getSessionId } from "./sessionManager";
import { AssetLoader } from "../utils/assetLoader";
import { isOpen } from "./widget";
import { client_config } from "./store";
import { scopedLocalStorage, scopedSessionStorage } from "../utils/storage";
import {
  BASE_URL,
  cityNameReplacerConfig,
  clientCityPropertyConfig,
  clientLanguageIndexConfig,
  clientsWithPrivacyMessage,
  clientUrlPatternsConfig,
  propertyNameReplacerConfig,
  searchParamsReplacerConfig,
} from "../utils/constants";
import { getDeviceType, regexForHousinghand } from "../utils/validators";
import { load } from "@fingerprintjs/botd";
import {
  capitalizeMatchingPart,
  formatSegment,
  stripHtmlExtensionFromUrl,
} from "../utils/functions";

export const socketManager = atom<Socket | null>(null);
export const userID = () => scopedLocalStorage.getItem("user_id") || "";
export const botPresetMessageString = atom<{
  original_message: string;
  translated_message: string;
}>({
  original_message: "Hello, how can I help you today?",
  translated_message: "",
});

export const messages = atom<any[]>([]);
export const pendingMessages = atom<ChatMessage[]>([]);
export const chatMessages = atom<
  { message: string; role: string; timestamp: string; language: string }[]
>([]);
export const showInput = atom<boolean>(true);
export const client_id = atom<string | null>(null);
export const user_email = atom<string | null>(null);
export const is_logged_in = atom<boolean>(false);
export const showThinking = atom<boolean>(false);
export const showSocketLoader = atom<boolean>(true);
export const activeAgentIndex = atom<number[]>([]);
export const activeAssistantIndex = atom<number[]>([]);
export const clientAiEnabled = atom<boolean>(true);
export const sessionAiEnabled = atom<boolean>(true);
export const activeTyping = atom<boolean>(true);
export const property_name = atom<string | null>(null);
export const city_name = atom<string | null>(null);
export const room_type = atom<string | null>(null);
export const user_details_form_submitted = atom<boolean>(false);
export const input_field_enabled = atom<boolean>(false);
export const selected_communication = atom<string>("message");
export const is_feedback_collected = atom<boolean>(false);
export const is_feedback_form_open = atom<boolean>(false);
export const is_feedback_form_enabled = atom<boolean>(false);
export const selected_language = atom<{ name: string; code: string }>({
  name: "",
  code: "",
});
export const bot_version = atom<string | null>(null);
export const send_message_input_value = atom<string>("");
const historyLoaded = atom<boolean>(false);
export const is_bot_detected = atom<boolean>(false);
export const tracking_info = atom<{
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  gclid: string | null;
  fbclid: string | null;
  msclkid: string | null;
}>({
  utm_source: null,
  utm_medium: null,
  utm_campaign: null,
  utm_term: null,
  utm_content: null,
  gclid: null,
  fbclid: null,
  msclkid: null,
});
export const is_new_chat_loading = atom<boolean>(false);
export const is_privacy_policy_accepted = atom<boolean>(
  JSON.parse(
    scopedLocalStorage?.getItem("is_privacy_policy_accepted") || "false"
  )
);
export const attachments = atom<any[]>([]);
export const attachmentModalOpen = atom<boolean>(false);
export const attachmentModalInitialIndex = atom<number>(0);
export const attachmentModalTimestamp = atom<string>("");

const updateActiveTyping = () => {
  activeTyping.set(clientAiEnabled.get() && sessionAiEnabled.get());
};

export const setClientAiEnabled = (value: boolean) => {
  clientAiEnabled.set(value);
  updateActiveTyping();
};

export const setBotPresetMessageString = (value: {
  original_message: string;
  translated_message: string;
}) => {
  botPresetMessageString?.set(value);
};

export const detectBot = async () => {
  try {
    const botdPromise = load();
    const botd = await botdPromise;
    const result = botd?.detect();

    if (result?.bot) {
      is_bot_detected?.set(true);
    } else {
      is_bot_detected?.set(false);
    }
  } catch (error) {
    is_bot_detected?.set(false);
  }
};

export const getDynamicPropertyMessage = (url?: string): string | null => {
  const config = client_config.get();
  const dynamicMessaging = config?.dynamic_property_messaging;
  if (!dynamicMessaging?.rows?.length) return null;

  const currentUrl = url || window.location.href;
  const toOriginPath = (value: string) => {
    const parsed = new URL(stripHtmlExtensionFromUrl(value));
    return parsed.origin + parsed.pathname;
  };
  const currentNormalized = toOriginPath(currentUrl);

  const matchedRow = dynamicMessaging.rows.find((row: any) => {
    try {
      return toOriginPath(row?.page_link || "") === currentNormalized;
    } catch {
      return false;
    }
  });

  if (!matchedRow) return null;

  if (matchedRow.rendered_message) return matchedRow.rendered_message;

  if (matchedRow.property_name && matchedRow.city_name && dynamicMessaging.property_and_city_message) {
    return dynamicMessaging.property_and_city_message
      .replace("{{property}}", matchedRow.property_name)
      .replace("{{city}}", matchedRow.city_name);
  }

  if (matchedRow.city_name && dynamicMessaging.city_only_message) {
    return dynamicMessaging.city_only_message.replace("{{city}}", matchedRow.city_name);
  }

  return null;
};

export const setBotPresetMessage = (url?: string) => {
  const currentMessages = messages.get();
  const hasUserOrServerMessages = currentMessages.some(
    (msg: any) =>
      msg.role === "user" || msg.role === "assistant" || msg.role === "agent"
  );

  if (!hasUserOrServerMessages) {
    const newWelcome: any = {
      message:
        botPresetMessageString?.get()?.original_message ||
        "Hello, how can I help you today?",
      translated_message:
        botPresetMessageString?.get()?.translated_message || "",
      role: "assistant",
      timestamp: new Date().toISOString(),
      property_json: null,
      language: scopedLocalStorage.getItem("lang") || "en",
    };
    currentMessages.push(newWelcome);

    const dynamicMessage = getDynamicPropertyMessage(url);

    if (dynamicMessage) {
      currentMessages.push({
        message: dynamicMessage,
        role: "assistant",
        timestamp: new Date(Date.now() + 5000).toISOString(),
        property_json: null,
        language: scopedLocalStorage.getItem("lang") || "en",
      });
    } else {
      let propertyName = property_name.get();
      let cityName = city_name.get();
      if (propertyName && cityName) {
        currentMessages.push({
          message: `I can see you're looking at **${propertyName}** in **${cityName}**. Are you just browsing or do you need any help?`,
          role: "assistant",
          timestamp: new Date(Date.now() + 5000).toISOString(),
          property_json: null,
          language: scopedLocalStorage.getItem("lang") || "en",
        });
      } else if (cityName) {
        let message = "";
        if ((cityName === "Milan" || cityName === "Gdańsk" || cityName === "Kraków") && client_id.get() === "milestone") {
          message = `I see you're exploring property at **${cityName}**. Are you just browsing or is there anything I can help you with?`;
        }else{
          message = `I see you're exploring properties in **${cityName}**. Are you just browsing or is there anything I can help you with?`;
        }
        currentMessages.push({
          message: message,
          role: "assistant",
          timestamp: new Date(Date.now() + 5000).toISOString(),
          property_json: null,
          language: scopedLocalStorage.getItem("lang") || "en",
        });
      }
    }
    messages.set(currentMessages);
  }
};

export const setBotPresetMessageForRoomType = () => {
  const currentMessages = messages?.get();
  const isOnlyWelcomeMessage =
    currentMessages?.[0]?.role === "assistant" && currentMessages?.length === 1;
  const isHouseOfSocial = client_id?.get() === "houseofsocial";

  if (isOnlyWelcomeMessage && isHouseOfSocial) {
    const parsedUrl = new URL(window?.location?.href);

    const segments = parsedUrl?.pathname
      ?.split("/")
      ?.filter((segment) => segment !== "");

    const hasStudentHousesViewRoom =
      segments?.length >= 2 &&
      segments?.[0] === "student-houses" &&
      segments?.[1]?.includes("view-room");

    if (hasStudentHousesViewRoom) {
      const roomHeaderContainer = document?.querySelector(
        ".hos-room-header__container"
      );

      const roomHeaderTitle =
        roomHeaderContainer?.querySelector("h1.background-ring");

      room_type?.set(roomHeaderTitle?.textContent?.trim() || null);
    }

    const roomType = room_type?.get();

    if (roomType) {
      messages?.set([
        ...(messages?.get() || []),
        {
          message: `Hi there! I see you're interested in room type **${roomType}**, is there anything I can help you with?`,
          role: "assistant",
          timestamp: new Date(Date.now() + 5000).toISOString(),
          property_json: null,
          language: scopedLocalStorage.getItem("lang") || "en",
        },
      ]);
    }
  }
};

const playMessageSound = () => {
  AssetLoader.getInstance().playNotification();
};

export const setUserEmail = (email: string | null) => {
  user_email.set(email);
  is_logged_in.set(!!email);
};

export const setBotVersion = (version: string | null) => {
  bot_version.set(version);
};

const USER_ID_KEY = "user_id";
const SOCKET_TOKEN_KEY = "socket_token";
const IDENTITY_LOCK_KEY = "socket_identity_lock";
const TOKEN_EXPIRY_SKEW_MS = 60_000;
const IDENTITY_LOCK_TTL_MS = 2000;

type GuestIdentity = { user_id: string; token: string };
type WidgetIdentity = { user_id: string; token?: string };

let identityResetThisLoad = false;

const isLoggedInVisitor = () => !!(is_logged_in.get() && user_email.get());

const clearIdentity = () => {
  scopedLocalStorage.removeItem(USER_ID_KEY);
  scopedLocalStorage.removeItem(SOCKET_TOKEN_KEY);
};

const writeIdentity = (user_id: string, token: string) => {
  scopedLocalStorage.setItem(USER_ID_KEY, user_id);
  scopedLocalStorage.setItem(SOCKET_TOKEN_KEY, token);
};

const readIdentity = (): GuestIdentity | null => {
  const user_id = scopedLocalStorage.getItem(USER_ID_KEY);
  const token = scopedLocalStorage.getItem(SOCKET_TOKEN_KEY);
  if (!user_id || !token) {
    if (user_id || token) {
      clearIdentity();
    }
    return null;
  }
  return { user_id, token };
};

const isTokenExpiring = (token: string) => {
  try {
    const payload = token.split(".")[1];
    if (!payload) return true;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
    const parsed = JSON.parse(atob(padded));
    if (typeof parsed?.exp !== "number") return true;
    return parsed.exp * 1000 - Date.now() <= TOKEN_EXPIRY_SKEW_MS;
  } catch {
    return true;
  }
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const takeIdentityLock = () => {
  const now = Date.now();
  const expires = Number(scopedLocalStorage.getItem(IDENTITY_LOCK_KEY));
  if (Number.isFinite(expires) && expires > now) {
    return false;
  }
  scopedLocalStorage.setItem(
    IDENTITY_LOCK_KEY,
    String(now + IDENTITY_LOCK_TTL_MS)
  );
  return true;
};

const releaseIdentityLock = () => {
  scopedLocalStorage.removeItem(IDENTITY_LOCK_KEY);
};

const waitForIdentityFromOtherTab = async () => {
  const deadline = Date.now() + IDENTITY_LOCK_TTL_MS;
  while (Date.now() < deadline) {
    const identity = readIdentity();
    if (identity && !isTokenExpiring(identity.token)) {
      return identity;
    }
    await sleep(50);
  }
  return readIdentity();
};

const mintIdentity = async (): Promise<GuestIdentity> => {
  const response = await axios.get(`${BASE_URL}/fe/user_id`, {
    params: {
      tenant_id: client_id.get() || "",
    },
  });
  const user_id = response.data?.user_id;
  const token = response.data?.token;
  if (!user_id || !token) {
    throw new Error("Failed to mint socket identity");
  }
  writeIdentity(user_id, token);
  return { user_id, token };
};

const ensureIdentity = async (): Promise<WidgetIdentity | null> => {
  if (isLoggedInVisitor()) {
    return { user_id: user_email.get() as string };
  }

  const stored = readIdentity();
  if (stored && !isTokenExpiring(stored.token)) {
    return stored;
  }

  clearIdentity();

  if (!takeIdentityLock()) {
    const fromOtherTab = await waitForIdentityFromOtherTab();
    if (fromOtherTab && !isTokenExpiring(fromOtherTab.token)) {
      return fromOtherTab;
    }
    if (!takeIdentityLock()) {
      return readIdentity();
    }
  }

  try {
    return await mintIdentity();
  } finally {
    releaseIdentityLock();
  }
};

export const getUserID = async () => {
  if (isLoggedInVisitor()) {
    return user_email.get();
  }
  const identity = await ensureIdentity();
  return identity?.user_id;
};

let isConnecting = false;
const MAX_RECONNECT_ATTEMPTS = 10;
const BASE_RECONNECT_DELAY = 1000;
const MAX_DELAY = 30000;

const cleanupSocket = (socket: Socket | null) => {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    showSocketLoader.set(true);
    showThinking.set(false);
    sessionAiEnabled.set(true);
    updateActiveTyping();
    socketManager.set(null);
  }
};

const createSocket = (user_id: string, session_id: string, token?: string) => {
  const urlParams = new URLSearchParams(window.location.search);
  const verbaflo_lead_id = urlParams.get("verbaflo_lead_id");

  const query: any = {
    user_id,
    tenant_id: client_id.get() || "",
    session_id,
    logged_in: is_logged_in.get(),
    tracking_url: stripHtmlExtensionFromUrl(window.location.href),
    bot_version: bot_version.get() || "",
    device_type: getDeviceType(),
  };

  if (verbaflo_lead_id) {
    query.lead_id = verbaflo_lead_id;
  }

  return io(BASE_URL, {
    query,
    ...(token ? { auth: { token } } : {}),
    transports: ["websocket"],
    reconnection: true,
    reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
    reconnectionDelay: BASE_RECONNECT_DELAY,
    reconnectionDelayMax: MAX_DELAY,
  });
};

const clearChatStateForOldIdentity = () => {
  messages.set([]);
  chatMessages.set([]);
  pendingMessages.set([]);
  historyLoaded.set(false);
  scopedSessionStorage.removeItem("chat_session");
};

const handleIdentityRejection = async (socket: Socket) => {
  cleanupSocket(socket);
  clearIdentity();
  clearChatStateForOldIdentity();

  if (identityResetThisLoad) {
    console.error("Socket identity rejected after reset; stopping retries");
    showSocketLoader.set(true);
    isConnecting = false;
    return;
  }

  identityResetThisLoad = true;
  isConnecting = false;
  await connectSocket();
};

const manageFeedbackFormVisibility = (
  is_feedback_event_collected: boolean,
  current_feedback_submission_state: string | null
) => {
  const shouldShowForm =
    !is_feedback_event_collected &&
    current_feedback_submission_state === "true";
  const shouldCollectFeedback = is_feedback_event_collected;
  is_feedback_form_open.set(shouldShowForm);
  is_feedback_collected.set(shouldCollectFeedback);
  scopedSessionStorage?.setItem(
    "is_feedback_form_open",
    JSON?.stringify(shouldShowForm)
  );
};

const setupSocketEvents = (socket: Socket) => {
  let gotChatData = false;

  socket.on("connect", () => {
    console.log("Connected to socket.io server");
    showSocketLoader.set(false);
    socketManager.set(socket);
    isConnecting = false;


  });

  socket.on("initial_messages", (data: any[]) => {
    // Only process if this is still the active socket
    if (socket !== socketManager.get()) return;
    if (!data || !Array.isArray(data) || data.length === 0) return;
    gotChatData = true;

    messages.set([]);
    const receivedMessages = data.map((msg: any) => ({
      message: msg.message,
      role: msg.role,
      property_json: msg.property_json?.length ? msg.property_json : null,
      timestamp: msg.timestamp,
      agentName: msg.agent_name || "",
      quick_replies: msg.quick_replies || [],
      kind: msg?.kind || "",
      translated_message: msg?.translated_message || "",
      attachments: msg?.attachments || [],
    }));

    messages.set(receivedMessages);
    historyLoaded.set(true);
  });

  socket.on("message", (data: any) => {
    // Only process if this is still the active socket
    if (socket !== socketManager.get()) return;
    if (!data) return;

    const receivedMessage = {
      message: data.message,
      role: data.role,
      property_json: data.property_json?.length ? data.property_json : null,
      timestamp: data.timestamp,
      agentName: data.agent_name || "",
      quick_replies: data.quick_replies || [],
      kind: data?.kind || "",
      message_kind: data?.message_kind || "",
      translated_message: data?.translated_message || "",
      attachments: data?.attachments || [],
    };

    const currentMessages = [...messages.get(), receivedMessage];
    messages.set(currentMessages);
    if (receivedMessage?.message_kind !== "filler_message") {
      showThinking.set(false);
    }
    activeAgentIndex.set(shouldDisplayAgentActive(currentMessages));
    activeAssistantIndex.set(shouldDisplayAssistantActive(currentMessages));

    if (!isOpen.get()) {
      pendingMessages.set([...pendingMessages.get(), receivedMessage]);
    }

    if (toggleShowInput(messages.get())) {
      showInput.set(true);
    }

    if (checkUserAssistantInteraction(messages.get())) {
      updateUtmDetails();
    }

    if (
      (client_id.get() === "vita" ||
        client_id.get() === "houseofsocial" ||
        client_id.get() === "nowstudents" ||
        client_id.get() === "herestudents" ||
        client_id.get() === "aparto") &&
      checkIfUserHasMessaged(messages.get(), "chat_message")
    ) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "live_chat_started",
        chatProvider: "verbal",
        chatAction: "Message Sent",
      });
    }

    playMessageSound();
  });

  socket.on("event", (data: any) => {
    if (socket !== socketManager.get()) return;
    if (!data) return;

    if (data.event_type === "toggle_communication_channel") {
      const toggleStatus = data?.event_details?.toggle_status;
      const scope = data?.event_details?.scope;

      if (scope === "client") {
        clientAiEnabled.set(toggleStatus);
      } else {
        sessionAiEnabled.set(toggleStatus);
      }
      updateActiveTyping();
      showThinking.set(false);
    }
  });

  socket.on("events", (data: any) => {
    if (socket !== socketManager.get()) return;
    if (!data) return;
    if (data.event_type === "toggle_communication_channel") {
      const toggleStatus = data?.event_details?.toggle_status;
      const scope = data?.event_details?.scope;

      if (scope === "client") {
        clientAiEnabled.set(toggleStatus);
      } else {
        sessionAiEnabled.set(toggleStatus);
      }
      updateActiveTyping();
      showThinking.set(false);
    }
  });

  socket.on("feedback_collected", (data: any) => {
    if (socket !== socketManager.get()) return;
    if (!data) return;
    const is_feedback_event_collected = JSON.parse(data);
    const current_feedback_submission_state = scopedSessionStorage?.getItem(
      "is_feedback_form_open"
    );
    manageFeedbackFormVisibility(
      is_feedback_event_collected,
      current_feedback_submission_state
    );
  });

  socket.on("collect_feedback", () => {
    if (socket !== socketManager?.get()) return;
    if (!is_feedback_collected?.get()) {
      setTimeout(() => {
        is_feedback_form_open?.set(true);
        scopedSessionStorage?.setItem("is_feedback_form_open", "true");
      }, 5000);
    }
  });

  socket.on("disconnect", (reason: any) => {
    console.warn(`Socket.io disconnected disconnect: ${reason}`);
    if (socket !== socketManager.get()) return;

    const isRejection =
      reason === "io server disconnect" &&
      !gotChatData &&
      !isLoggedInVisitor();

    socketManager.set(null);
    showThinking.set(false);
    sessionAiEnabled.set(true);
    updateActiveTyping();
    showSocketLoader.set(true);
    isConnecting = false;

    if (isRejection) {
      handleIdentityRejection(socket);
      return;
    }

    setTimeout(() => {
      socket.connect();
    }, 3000);
  });
};

export const connectSocket = async () => {
  if (socketManager.get()?.connected || isConnecting) {
    console.log(
      "Socket already connected or connecting, skipping connection attempt"
    );
    return;
  }

  isConnecting = true;

  try {
    const session_id = getSessionId();
    const identity = await ensureIdentity();
    const user_id = identity?.user_id;
    const loggedIn = isLoggedInVisitor();

    if (!user_id) {
      throw new Error("Failed to get user ID");
    }

    if (!loggedIn && !identity?.token) {
      console.error("Missing socket token, skipping connection");
      isConnecting = false;
      showSocketLoader.set(false);
      return;
    }

    // Handle initial session tracking
    const chat_session = scopedSessionStorage.getItem("chat_session");
    if (user_id && session_id && chat_session !== "true") {
      try {
        await fetch(`${BASE_URL}/fe/visitor_count/${client_id.get()}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            // "ngrok-skip-browser-warning": "skip-browser-warning",
          },
          body: JSON.stringify({ user_id, session_id }),
        });
        scopedSessionStorage.setItem("chat_session", "true");
      } catch (error) {
        console.error("Failed to update visitor count:", error);
        scopedSessionStorage.setItem("chat_session", "true");
      }
    }

    cleanupSocket(socketManager.get());

    const socket = createSocket(
      user_id,
      session_id,
      loggedIn ? undefined : identity?.token
    );

    setupSocketEvents(socket);

    isOpen.subscribe((open: boolean) => {
      if (open) {
        pendingMessages.set([]);
      }
    });
  } catch (error) {
    console.error("Error in connectSocket:", error);
    isConnecting = false;
    showSocketLoader.set(false);
  }
};

export const resetConnection = async (config: any) => {
  try {
    await axios.post(`${BASE_URL}/fe/flush_session_id`, {
      user_id:
        is_logged_in.get() && user_email.get()
          ? user_email.get()
          : scopedLocalStorage.getItem("user_id"),
    });
  } catch {
    console.error("Failed to flush session");
  }

  cleanupSocket(socketManager.get());

  scopedLocalStorage.removeItem("session_info");
  clearIdentity();
  scopedLocalStorage.removeItem("user_details_form_submitted");
  scopedLocalStorage.removeItem("updateDataLayer");
  scopedLocalStorage.removeItem("user_form_details");
  scopedSessionStorage.removeItem("chat_session");

  showInput.set(input_field_enabled.get());
  sessionAiEnabled.set(true);
  updateActiveTyping();
  showThinking.set(false);
  messages.set([]);
  chatMessages.set([]);
  activeAgentIndex.set([]);
  activeAssistantIndex.set([]);

  if (config?.form_fields?.enabled) {
    user_details_form_submitted.set(false);
  } else {
    user_details_form_submitted.set(true);
  }

  setTimeout(() => {
    connectSocket();
  }, 2000);
};

export const sendMessage = () => {
  const socket = socketManager.get();
  if (socket && socket.connected) {
    let currentMessages = [...chatMessages.get()];
    socket.emit("message", currentMessages);
    chatMessages.set([]);
  } else {
    console.warn("Socket.io not connected. Message not sent.");
  }
};

export const shouldDisplayAgentActive = (messages: ChatMessage[]) => {
  let agentIndexes: number[] = [];
  const filteredMessages = messages
    .map((message, index) => ({ message, originalIndex: index }))
    .filter(
      (message: { message: ChatMessage }) => message.message.role !== "user"
    );

  if (filteredMessages.length === 0) return agentIndexes;
  filteredMessages.forEach((filteredMessage, filteredIndex) => {
    if (
      filteredMessage.message.role === "agent" &&
      filteredMessages[filteredIndex - 1]?.message.role === "assistant"
    ) {
      agentIndexes.push(filteredMessage.originalIndex);
    }
  });
  return agentIndexes;
};

export const shouldDisplayAssistantActive = (messages: ChatMessage[]) => {
  let assistantIndex: number[] = [];
  const filteredMessages = messages
    .map((message, index) => ({ message, originalIndex: index }))
    .filter(
      (message: { message: ChatMessage }) => message.message.role !== "user"
    );

  if (filteredMessages.length === 0) return assistantIndex;
  filteredMessages.forEach((filteredMessage, filteredIndex) => {
    if (
      filteredMessage.message.role === "assistant" &&
      filteredMessages[filteredIndex - 1]?.message.role === "agent"
    ) {
      assistantIndex.push(filteredMessage.originalIndex);
    }
  });
  return assistantIndex;
};

const checkIfCityExists = (cityName: string, clientCode: string) => {
  return Object.keys(clientCityPropertyConfig?.[clientCode])?.includes(
    cityName
  );
};

const checkIfPropertyInCity = (
  property: string,
  cityName: string,
  clientCode: string
) => {
  return clientCityPropertyConfig?.[clientCode]?.[cityName]?.includes(property);
};

const propertyNameExtractor = (
  cityName: string,
  clientCode: string,
  index: number
) => {
  return clientCityPropertyConfig?.[clientCode]?.[cityName]?.[index];
};

const getCityNameByPropertyName = (
  propertyName: string,
  clientCode: string
) => {
  let cityName = null;

  const cities = Object?.keys(clientCityPropertyConfig?.[clientCode]);

  cities?.forEach((city) => {
    clientCityPropertyConfig?.[clientCode]?.[city]?.find((property) => {
      if (property?.includes(propertyName)) {
        cityName = city;
      }
    });
  });

  return cityName;
};

const handleLondonistLocation = (segments: string[], clientCode: string) => {
  if (segments?.length === 1 && segments?.[0] === "residences") {
    city_name?.set("London");
  } else if (segments?.length === 2 && segments?.[0] === "residences") {
    const property = segments?.[1]?.split("_")?.[0];

    let formattedPropertyName = capitalizeMatchingPart(
      formatSegment(property),
      "ii"
    );

    formattedPropertyName = capitalizeMatchingPart(
      formattedPropertyName,
      "iq" 
    );

    formattedPropertyName =
      propertyNameReplacerConfig?.[clientCode]?.[formattedPropertyName] ??
      formattedPropertyName;

    const isPropertyInCity = checkIfPropertyInCity(
      formattedPropertyName,
      "London",
      clientCode
    );

    city_name?.set("London");
    property_name?.set(isPropertyInCity ? formattedPropertyName : "");
  }
};

const handleStudentcastleLocation = (
  segments: string[],
  clientCode: string,
  clientConfig: any
) => {
  let formattedCityName = formatSegment(segments?.[clientConfig?.cityIndex], 0);

  const isCityExists = checkIfCityExists(formattedCityName, clientCode);

  const propertyName = propertyNameExtractor(formattedCityName, clientCode, 0);

  city_name?.set(isCityExists ? formattedCityName : "");
  property_name?.set(propertyName);
};

const handleFusiongroupLocation = (
  segments: string[],
  clientCode: string,
  clientConfig: any
) => {
  let formattedCityName = formatSegment(segments?.[clientConfig?.cityIndex]);

  const isCityExists = checkIfCityExists(formattedCityName, clientCode);

  const propertyName = propertyNameExtractor(formattedCityName, clientCode, 0);

  city_name?.set(isCityExists ? formattedCityName : "");
  property_name?.set(propertyName);
};

const handleScapeLocation = (
  segments: string[],
  clientCode: string,
  clientConfig: any
) => {
  let formattedCityName = formatSegment(segments?.[clientConfig?.cityIndex], 2);

  const isCityLondon = formattedCityName === "London";
  const isCityGuildford = formattedCityName === "Guildford";
  const isCityLeeds = formattedCityName === "Leeds";

  if (isCityLondon) {
    city_name?.set("London");
    property_name?.set("");
  } else if (isCityGuildford || isCityLeeds) {
    const isCityExists = checkIfCityExists(formattedCityName, clientCode);

    const propertyName = propertyNameExtractor(
      formattedCityName,
      clientCode,
      0
    );

    city_name?.set(isCityExists ? formattedCityName : "");
    property_name?.set(propertyName);
  } else {
    let formattedCityName = formatSegment(
      segments?.[clientConfig?.cityIndex],
      2,
      true
    );

    const propertyName = clientCityPropertyConfig?.[clientCode]?.[
      "London"
    ]?.includes(formattedCityName)
      ? formattedCityName
      : "";

    city_name?.set(propertyName ? "London" : "");
    property_name?.set(propertyName);
  }
};

const handleCloudstudentLocation = (
  segments: string[],
  clientCode: string,
  clientConfig: any
) => {
  const isAllCitiesPage =
    segments?.[0] === "student-accommodation" && segments?.length === 1;

  if (isAllCitiesPage) {
    city_name?.set("");
    property_name?.set("");
    return;
  }

  let formattedCityName = formatSegment(segments?.[clientConfig?.cityIndex]);
  let formattedPropertyName = formatSegment(
    segments?.[clientConfig?.propertyIndex]
  );

  formattedPropertyName =
    propertyNameReplacerConfig?.[clientCode]?.[formattedPropertyName] ??
    formattedPropertyName;

  const isCityExists = checkIfCityExists(formattedCityName, clientCode);

  const isPropertyInCity = checkIfPropertyInCity(
    formattedPropertyName,
    formattedCityName,
    clientCode
  );

  city_name?.set(isCityExists ? formattedCityName : "");
  property_name?.set(isPropertyInCity ? formattedPropertyName : "");
};

const handleModaLocation = (
  segments: string[],
  clientCode: string,
  clientConfig: any,
  searchParams: string[]
) => {
  let formattedCityName = formatSegment(segments?.[clientConfig?.cityIndex]);
  let formattedPropertyName = formatSegment(
    segments?.[clientConfig?.propertyIndex]
  );

  formattedCityName =
    cityNameReplacerConfig?.[clientCode]?.[formattedCityName] ??
    formattedCityName;
  formattedPropertyName =
    propertyNameReplacerConfig?.[clientCode]?.[formattedPropertyName] ??
    formattedPropertyName;

  let isCityExists = checkIfCityExists(formattedCityName, clientCode);
  let isPropertyInCity = checkIfPropertyInCity(
    formattedPropertyName,
    formattedCityName,
    clientCode
  );

  if (searchParams?.length > 0) {
    const formattedParams = formatSegment(searchParams?.[1]);
    const formattedPropertyParams =
      propertyNameReplacerConfig?.[clientCode]?.[formattedParams] ??
      formattedParams;
    Object?.keys(clientCityPropertyConfig?.[clientCode])?.forEach((city) => {
      if (checkIfPropertyInCity(formattedPropertyParams, city, clientCode)) {
        formattedCityName = city;
        formattedPropertyName = formattedPropertyParams;
        isCityExists = true;
        isPropertyInCity = true;
      }
    });
  }

  city_name?.set(isCityExists ? formattedCityName : "");
  property_name?.set(isPropertyInCity ? formattedPropertyName : "");
};

const handleMilestoneLocation = (
  segments: string[],
  clientCode: string,
  searchParams: string[],
  hashParams: string[],
  hostname?: string,
  clientConfig?: any
) => {
  if (hostname === "triberaliving.com") {
    let formattedCityName = formatSegment(segments?.[clientConfig?.cityIndex]);
    let formattedPropertyName = formatSegment(
      segments?.[clientConfig?.propertyIndex]
    );

    formattedCityName =
      cityNameReplacerConfig?.[clientCode]?.[formattedCityName] ??
      formattedCityName;

    formattedPropertyName =
      propertyNameReplacerConfig?.[clientCode]?.[formattedPropertyName] ??
      "";

    const isCityExists = checkIfCityExists(formattedCityName, clientCode);
    let isPropertyInCity = checkIfPropertyInCity(
      formattedPropertyName,
      formattedCityName,
      clientCode
    );
    if (!isPropertyInCity && isCityExists && segments?.[clientConfig?.propertyIndex]) {
      const cityProperties = clientCityPropertyConfig?.[clientCode]?.[formattedCityName];
      if (cityProperties?.length === 1) {
        formattedPropertyName = cityProperties[0];
        isPropertyInCity = true;
      }
    }

    city_name?.set(isCityExists ? formattedCityName : "");
    property_name?.set(isPropertyInCity ? formattedPropertyName : "");
    return;
  }

  let formattedPropertyName = formatSegment(segments?.[1]);
  let formattedCityName = "";

  const decodedSearchParams = decodeURIComponent(searchParams?.[0]);
  const decodedHashParams = decodeURIComponent(hashParams?.[0]);

  const extractCityPropertyFromParams =
    searchParamsReplacerConfig?.[clientCode]?.[decodedSearchParams] ||
    searchParamsReplacerConfig?.[clientCode]?.[decodedHashParams];

  if (
    extractCityPropertyFromParams?.city &&
    extractCityPropertyFromParams?.property
  ) {
    city_name?.set(extractCityPropertyFromParams?.city);
    property_name?.set(extractCityPropertyFromParams?.property);
    return;
  }

  formattedPropertyName =
    propertyNameReplacerConfig?.[clientCode]?.[formattedPropertyName] ??
    formattedPropertyName;

  Object?.keys(clientCityPropertyConfig?.[clientCode])?.forEach((city) => {
    const cityNameReplacement = cityNameReplacerConfig?.[clientCode]?.[city];

    const cityName = cityNameReplacement ?? city;

    if (formattedPropertyName?.includes(cityName)) {
      formattedCityName = city;
    }
  });

  city_name?.set(formattedCityName);
  property_name?.set(formattedPropertyName);
};

const handleCollegiateLocation = (
  segments: string[],
  clientCode: string,
  clientConfig: any
) => {
  let formattedCityName = formatSegment(segments?.[clientConfig?.cityIndex]);
  let formattedPropertyName = formatSegment(
    segments?.[clientConfig?.propertyIndex]
  );

  formattedPropertyName =
    propertyNameReplacerConfig?.[clientCode]?.[formattedPropertyName] ??
    formattedPropertyName;

  formattedCityName =
    cityNameReplacerConfig?.[clientCode]?.[formattedCityName] ??
    formattedCityName;

  const isCityExists = checkIfCityExists(formattedCityName, clientCode);
  const isPropertyInCity = checkIfPropertyInCity(
    formattedPropertyName,
    formattedCityName,
    clientCode
  );
  city_name?.set(isCityExists ? formattedCityName : "");
  property_name?.set(isPropertyInCity ? formattedPropertyName : "");
};

const handleCapitolstudentsLocation = (
  segments: string[],
  clientCode: string,
  clientConfig: any
) => {
  let formattedCityName = formatSegment(segments?.[clientConfig?.cityIndex]);
  let formattedPropertyName = formatSegment(
    segments?.[clientConfig?.propertyIndex]
  );

  const staticPropertyNames = [
    "Westwood Student Mews",
    "Clifton & Stewart House",
  ];

  const staticPropertyName = staticPropertyNames?.includes(
    formattedPropertyName
  )
    ? formattedPropertyName
    : formattedPropertyName?.split(" ")?.slice(1)?.join(" ") ?? "";

  formattedPropertyName =
    propertyNameReplacerConfig?.[clientCode]?.[staticPropertyName] ??
    staticPropertyName;

  const isCityExists = checkIfCityExists(formattedCityName, clientCode);

  const isPropertyInCity = checkIfPropertyInCity(
    formattedPropertyName,
    formattedCityName,
    clientCode
  );

  city_name?.set(isCityExists ? formattedCityName : "");
  property_name?.set(isPropertyInCity ? formattedPropertyName : "");
};

const handleStudentfmLocation = (
  segments: string[],
  clientCode: string,
  clientConfig: any
) => {
  let formattedCityName = formatSegment(segments?.[clientConfig?.cityIndex]);
  let formattedPropertyName = formatSegment(
    segments?.[clientConfig?.propertyIndex]
  );
  const cityAndPropertyName = formatSegment(segments?.[1]);

  formattedCityName =
    cityNameReplacerConfig?.[clientCode]?.[cityAndPropertyName] ??
    formattedCityName;

  formattedPropertyName =
    propertyNameReplacerConfig?.[clientCode]?.[cityAndPropertyName] ??
    formattedPropertyName;

  const isCityExists = checkIfCityExists(formattedCityName, clientCode);

  const isPropertyInCity = checkIfPropertyInCity(
    formattedPropertyName,
    formattedCityName,
    clientCode
  );

  city_name?.set(isCityExists ? formattedCityName : "");
  property_name?.set(isPropertyInCity ? formattedPropertyName : "");
};

const handleHelloHomieLocation = (
  segments: string[],
  clientCode: string,
  clientConfig: any
) => {
  let formattedCityName = formatSegment(segments?.[clientConfig?.cityIndex]);
  let formattedPropertyName = formatSegment(
    segments?.[clientConfig?.propertyIndex]
  );

  if (segments[0] === "property") {
    if (formattedPropertyName?.split(" ")?.[0] === "Flat") {
      const parts = formattedPropertyName?.split(" ");
      if (parts[1]) parts[1] = `${parts[1]},`;
      const correctedPropertyName = parts?.join(" ");
      const replacedPropertyName =
        propertyNameReplacerConfig?.[clientCode]?.[correctedPropertyName] ??
        correctedPropertyName;
      formattedCityName =
        getCityNameByPropertyName(replacedPropertyName, clientCode) ?? "";
      formattedPropertyName = replacedPropertyName;
    } else {
      const replacedPropertyName =
        propertyNameReplacerConfig?.[clientCode]?.[formattedPropertyName] ??
        formattedPropertyName;
      formattedCityName =
        getCityNameByPropertyName(replacedPropertyName, clientCode) ?? "";
      formattedPropertyName = replacedPropertyName;
    }

    const isCityExists = checkIfCityExists(formattedCityName, clientCode);

    const isPropertyInCity = checkIfPropertyInCity(
      formattedPropertyName,
      formattedCityName,
      clientCode
    );

    city_name?.set(isCityExists ? formattedCityName : "");
    property_name?.set(isPropertyInCity ? formattedPropertyName : "");
  } else if (segments[0] === "city") {
    const isCityExists = checkIfCityExists(formattedCityName, clientCode);

    city_name?.set(isCityExists ? formattedCityName : "");
  }
};

const handlePropertySenseLocation = (
  segments: string[],
  clientCode: string
) => {
  const propertySlug = segments?.[1];

  if (!propertySlug) {
    return;
  }

  const cities = Object.keys(clientCityPropertyConfig?.[clientCode] || {}).sort(
    (a, b) => b.length - a.length
  );

  const slugParts = propertySlug.split("-");
  const slugWithoutUuid = slugParts.slice(0, -5).join("-");

  let foundCity = "";
  let propertyNameSlug = "";

  for (const city of cities) {
    const citySlug = city.toLowerCase().replace(/\s+/g, "-");

    const cityRegex = new RegExp(
      `-${citySlug}-|^${citySlug}-|-${citySlug}$`,
      "i"
    );
    if (cityRegex.test(slugWithoutUuid)) {
      foundCity = city;
      const cityIndex = slugWithoutUuid.toLowerCase().indexOf(citySlug);
      if (cityIndex !== -1) {
        propertyNameSlug = slugWithoutUuid
          .substring(0, cityIndex)
          .replace(/-$/, "");
      }
      break;
    }
  }

  let formattedPropertyName = formatSegment(propertyNameSlug);

  formattedPropertyName = formattedPropertyName.replace(/^-\s*/, "").trim();

  formattedPropertyName =
    propertyNameReplacerConfig?.[clientCode]?.[formattedPropertyName] ??
    formattedPropertyName;

  const formattedCityName =
    cityNameReplacerConfig?.[clientCode]?.[foundCity] ?? foundCity;

  const isCityExists = checkIfCityExists(formattedCityName, clientCode);

  let isPropertyInCity = checkIfPropertyInCity(
    formattedPropertyName,
    formattedCityName,
    clientCode
  );

  if (!isPropertyInCity && formattedPropertyName.match(/^Apt\s+\d/)) {
    const withDash = formattedPropertyName.replace(/^Apt\s+(\d)/, "Apt - $1");
    const withoutDash = formattedPropertyName.replace(
      /^Apt\s+-\s+(\d)/,
      "Apt $1"
    );

    isPropertyInCity =
      checkIfPropertyInCity(withDash, formattedCityName, clientCode) ||
      checkIfPropertyInCity(withoutDash, formattedCityName, clientCode);

    if (isPropertyInCity) {
      if (checkIfPropertyInCity(withDash, formattedCityName, clientCode)) {
        formattedPropertyName = withDash;
      } else if (
        checkIfPropertyInCity(withoutDash, formattedCityName, clientCode)
      ) {
        formattedPropertyName = withoutDash;
      }
    }
  }

  city_name?.set(isCityExists ? formattedCityName : "");

  if (formattedPropertyName && foundCity && isCityExists) {
    property_name?.set(formattedPropertyName);
  } else if (isPropertyInCity) {
    property_name?.set(formattedPropertyName);
  } else {
    property_name?.set("");
  }
};

const handleYppLocation = (
  segments: string[],
  clientCode: string,
  clientConfig: any
) => {
  if (["property", "properties"].includes(segments?.[0]) && segments?.length >= 2) {
    city_name?.set("");
    property_name?.set("");
    return;
  }

  let formattedCityName = formatSegment(segments?.[clientConfig?.cityIndex]);
  let formattedPropertyName =
    formatSegment(segments?.[clientConfig?.propertyIndex])
      ?.split(" ")
      ?.slice(1)
      ?.join(" ") ?? "";

  formattedCityName =
    cityNameReplacerConfig?.[clientCode]?.[formattedCityName] ??
    formattedCityName;

  const isCityExists = checkIfCityExists(formattedCityName, clientCode);

  const formattedCityFromPropertyName =
    formatSegment(segments?.[clientConfig?.propertyIndex])?.split(" ")?.[0] ??
    "";

  formattedPropertyName =
    propertyNameReplacerConfig?.[clientCode]?.[formattedPropertyName] ??
    formattedPropertyName;

  const isPropertyInCity = checkIfPropertyInCity(
    formattedPropertyName,
    formattedCityFromPropertyName,
    clientCode
  );

  city_name?.set(
    isCityExists ? formattedCityName : formattedCityFromPropertyName
  );

  property_name?.set(isPropertyInCity ? formattedPropertyName : "");
};

const handleDefaultLocation = (
  segments: string[],
  clientCode: string,
  clientConfig: any
) => {
  let formattedCityName = formatSegment(segments?.[clientConfig?.cityIndex]);
  let formattedPropertyName = formatSegment(
    segments?.[clientConfig?.propertyIndex]
  );

  formattedPropertyName =
    propertyNameReplacerConfig?.[clientCode]?.[formattedPropertyName] ??
    formattedPropertyName;

  formattedCityName =
    cityNameReplacerConfig?.[clientCode]?.[formattedCityName] ??
    formattedCityName;

  const isCityExists = checkIfCityExists(formattedCityName, clientCode);

  const isPropertyInCity = checkIfPropertyInCity(
    formattedPropertyName,
    formattedCityName,
    clientCode
  );

  if (
    clientConfig?.autoSelectSingleProperty &&
    clientCityPropertyConfig?.[clientCode]?.[formattedCityName]?.length === 1
  ) {
    formattedPropertyName = propertyNameExtractor(
      formattedCityName,
      clientCode,
      0
    );
    city_name?.set(isCityExists ? formattedCityName : "");
    property_name?.set(formattedPropertyName);
  } else {
    city_name?.set(isCityExists ? formattedCityName : "");
    property_name?.set(isPropertyInCity ? formattedPropertyName : "");
  }
};

const handleNeostudentsLocation = (
  segments: string[],
  clientCode: string,
  clientConfig: any
) => {
  let formattedCityName = formatSegment(segments?.[clientConfig?.cityIndex]);
  let formattedPropertyName = formatSegment(
    segments?.[clientConfig?.propertyIndex]
  );

  formattedCityName =
    cityNameReplacerConfig?.[clientCode]?.[formattedCityName] ??
    formattedCityName;

  const isCityExists = checkIfCityExists(formattedCityName, clientCode);

  const isPropertyInCity = checkIfPropertyInCity(
    formattedPropertyName,
    "Canterbury",
    clientCode
  );

  city_name?.set(isCityExists ? formattedCityName : "Canterbury");
  property_name?.set(isPropertyInCity ? formattedPropertyName : "");
};

const handleAlmerostudentLocation = (
  segments: string[],
  clientCode: string,
  clientConfig: any
) => {
  let formattedCityName = formatSegment(segments?.[clientConfig?.cityIndex]);
  let formattedPropertyName = formatSegment(
    segments?.[clientConfig?.propertyIndex]
  );

  formattedPropertyName =
    propertyNameReplacerConfig?.[clientCode]?.[formattedPropertyName] ??
    formattedPropertyName;

  formattedCityName =
    cityNameReplacerConfig?.[clientCode]?.[formattedCityName] ??
    formattedCityName;

  const isCityExists = checkIfCityExists(formattedCityName, clientCode);

  const isPropertyInCity = checkIfPropertyInCity(
    formattedPropertyName,
    formattedCityName,
    clientCode
  );

  city_name?.set(isCityExists ? formattedCityName : "");
  property_name?.set(isPropertyInCity ? formattedPropertyName : "");
};

const handleNowstudentsLocation = (
  segments: string[],
  clientCode: string,
  clientConfig: any
) => {
  let formattedCityName = formatSegment(segments?.[clientConfig?.cityIndex]);
  let formattedPropertyName = formatSegment(
    segments?.[clientConfig?.propertyIndex]
  );

  formattedPropertyName =
    propertyNameReplacerConfig?.[clientCode]?.[formattedPropertyName] ??
    formattedPropertyName;

  formattedCityName =
    cityNameReplacerConfig?.[clientCode]?.[formattedCityName] ??
    formattedCityName;

  const isCityExists = checkIfCityExists(formattedCityName, clientCode);

  const isPropertyInCity = checkIfPropertyInCity(
    formattedPropertyName,
    formattedCityName,
    clientCode
  );

  city_name?.set(isCityExists ? formattedCityName : "");
  property_name?.set(isPropertyInCity ? formattedPropertyName : "");
};

const handleLunastudentsLocation = (
  segments: string[],
  clientCode: string,
  clientConfig: any
) => {
  if (segments?.[0] !== "property" || !segments?.[clientConfig?.propertyIndex]) {
    city_name?.set("");
    property_name?.set("");
    return;
  }

  let formattedPropertyName = formatSegment(
    segments?.[clientConfig?.propertyIndex]
  );

  formattedPropertyName =
    propertyNameReplacerConfig?.[clientCode]?.[formattedPropertyName] ??
    formattedPropertyName;

  const formattedCityName =
    getCityNameByPropertyName(formattedPropertyName, clientCode) ?? "";

  const isCityExists = checkIfCityExists(formattedCityName, clientCode);

  const isPropertyInCity = checkIfPropertyInCity(
    formattedPropertyName,
    formattedCityName,
    clientCode
  );

  city_name?.set(isCityExists ? formattedCityName : "");
  property_name?.set(isPropertyInCity ? formattedPropertyName : "");
};

const handleUnionLocation = (
  segments: string[],
  clientCode: string,
  clientConfig: any
) => {
  let formattedCityName = formatSegment(
    segments?.[clientConfig?.cityIndex]
  );

  formattedCityName =
    cityNameReplacerConfig?.[clientCode]?.[formattedCityName] ??
    "";
  const pageTypeSegment = segments?.[2]?.toLowerCase()?.replace("/", "");
  
  let pageTypeText = null;
  if (pageTypeSegment === "student") {
    pageTypeText = "the floor plans";
  } else if (pageTypeSegment === "photos") {
    pageTypeText = "the property photos";
  } else if (pageTypeSegment === "amenities") {
    pageTypeText = "the amenities";
  }
  if (!pageTypeText) {
   formattedCityName = "";
  }

  city_name?.set(formattedCityName);
  property_name?.set(pageTypeText);
};

const handleYourtribeLocation = (
  segments: string[],
  clientCode: string
) => {
  if (segments?.[0] !== "buildings" || !segments?.[1]) {
    return;
  }

  let formattedPropertyName = formatSegment(segments?.[1]);

  formattedPropertyName =
    propertyNameReplacerConfig?.[clientCode]?.[formattedPropertyName] ??
    formattedPropertyName;

  const fullPropertyName = `YourTRIBE ${formattedPropertyName}`;

  const cityName = getCityNameByPropertyName(fullPropertyName, clientCode);

  city_name?.set(cityName ?? "");
  property_name?.set(cityName ? fullPropertyName : "");
};

export const extractCityAndPropertyName = (url: string) => {
  const parsedUrl = new URL(stripHtmlExtensionFromUrl(url));
  /**
   * Now we will have to use client_config to directly extract the message
   */
  let hostname = parsedUrl.hostname;
  const segments = parsedUrl.pathname
    .split("/")
    .filter((segment) => segment !== "");
  const searchParams = parsedUrl.search
    .substring(1)
    .split("&")
    .map((param) => param.split("=")[1])
    .filter((value) => value);
  const hashFragment = parsedUrl.hash.substring(1);
  const hashParams = hashFragment
    .split("&")
    .map((param) => param.split("=")[1])
    .filter((value) => value);

  if (client_id.get() === "collegiateeu") {
    if (url.includes("www.collegiate.it/en")) {
      hostname = "www.collegiate.it/en";
    } else if (url.includes("www.collegiate-ac.es/en")) {
      hostname = "www.collegiate-ac.es/en";
    } else if (url.includes("www.collegiate.it")) {
      hostname = "www.collegiate.it";
    } else if (url.includes("www.collegiate-ac.es")) {
      hostname = "www.collegiate-ac.es";
    }
  }
  const clientConfig = clientUrlPatternsConfig?.[hostname];

  const clientCode = clientConfig?.clientCode;

  if (!clientConfig) {
    return null;
  }

  switch (clientCode) {
    case "vita":
    case "downingstudents":
    case "railyard":
    case "hfs":
    case "universalstudentliving":
    case "essentialstudentliving":
    case "evostudent":
    case "urbanstudentlife":
    case "prestigestudentliving":
    case "mansionstudent":
      handleDefaultLocation(segments, clientCode, clientConfig);
      break;
    case "fusiongroup":
      handleFusiongroupLocation(segments, clientCode, clientConfig);
      break;
    case "scape":
      handleScapeLocation(segments, clientCode, clientConfig);
      break;
    case "studentcastle":
      handleStudentcastleLocation(segments, clientCode, clientConfig);
      break;
    case "cloudstudent":
      handleCloudstudentLocation(segments, clientCode, clientConfig);
      break;
    case "londonist":
      handleLondonistLocation(segments, clientCode);
      break;
    case "moda":
      handleModaLocation(segments, clientCode, clientConfig, searchParams);
      break;
    case "milestone":
      handleMilestoneLocation(segments, clientCode, searchParams, hashParams, hostname, clientConfig);
      break;
    case "collegiateeu":
      handleCollegiateLocation(segments, clientCode, clientConfig);
      break;
    case "capitolstudents":
      handleCapitolstudentsLocation(segments, clientCode, clientConfig);
      break;
    case "hellohomie":
      handleHelloHomieLocation(segments, clientCode, clientConfig);
      break;
    case "studentfm":
      handleStudentfmLocation(segments, clientCode, clientConfig);
      break;
    case "propertysense":
      handlePropertySenseLocation(segments, clientCode);
      break;
    case "ypp":
      handleYppLocation(segments, clientCode, clientConfig);
      break;
    case "neostudents":
      handleNeostudentsLocation(segments, clientCode, clientConfig);
      break;
    case "almerostudent":
      handleAlmerostudentLocation(segments, clientCode, clientConfig);
      break;
    case "nowstudents":
      handleNowstudentsLocation(segments, clientCode, clientConfig);
      break;
      case "unionatdearborn":
      case "unionatmidtown":
      handleUnionLocation(segments, clientCode, clientConfig);
      break;
    case "lunastudents":
      handleLunastudentsLocation(segments, clientCode, clientConfig);
      break;
    case "truestudent":
    case "iconinc":
    case "aparto":
      handleDefaultLocation(segments, clientCode, clientConfig);
      break;
    case "yourtribe":
      handleYourtribeLocation(segments, clientCode);
      break;
    default:
      handleDefaultLocation(segments, clientCode, clientConfig);
  }
};

export const extractUtmDetails = (url: string) => {
  const parsedUrl = new URL(url);
  const params = new URLSearchParams(parsedUrl.search);

  return {
    utm_source: params.get("utm_source") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || "",
    utm_term: params.get("utm_term") || "",
    utm_content: params.get("utm_content") || "",
    gclid: params.get("gclid") || "",
    fbclid: params.get("fbclid") || "",
    msclkid: params.get("msclkid") || "",
  };
};

export const loadUtmDetails = () => {
  const utmValues = extractUtmDetails(window?.location?.href);

  const {
    utm_source,
    utm_medium,
    utm_campaign,
    utm_term,
    utm_content,
    gclid,
    fbclid,
    msclkid,
  } = utmValues;

  const hasAnyUtm =
    utm_source ||
    utm_medium ||
    utm_campaign ||
    utm_term ||
    utm_content ||
    gclid ||
    fbclid ||
    msclkid;

  if (hasAnyUtm) {
    scopedSessionStorage?.setItem("tracking_info", JSON?.stringify(utmValues));
    tracking_info?.set({
      utm_source: utm_source || null,
      utm_medium: utm_medium || null,
      utm_campaign: utm_campaign || null,
      utm_term: utm_term || null,
      utm_content: utm_content || null,
      gclid: gclid || null,
      fbclid: fbclid || null,
      msclkid: msclkid || null,
    });
  } else {
    const stored = JSON?.parse(
      scopedSessionStorage?.getItem("tracking_info") || "{}"
    );
    tracking_info?.set({
      utm_source: stored?.utm_source || null,
      utm_medium: stored?.utm_medium || null,
      utm_campaign: stored?.utm_campaign || null,
      utm_term: stored?.utm_term || null,
      utm_content: stored?.utm_content || null,
      gclid: stored?.gclid || null,
      fbclid: stored?.fbclid || null,
      msclkid: stored?.msclkid || null,
    });
  }
};

export function checkIfUserHasMessaged(
  messages: ChatMessage[],
  type: "user_message" | "chat_message"
) {
  if (type === "user_message") {
    const userMessageFound = messages?.filter(
      (message) => message?.role === "user"
    );
    const totalMessages = messages?.length;
    return userMessageFound?.length > 0 && totalMessages >= 3;
  } else if (type === "chat_message") {
    if (scopedLocalStorage.getItem("updateDataLayer") === "true") {
      return false;
    }

    for (let i = 0; i < messages.length; i++) {
      const { role } = messages[i];

      if (role === "user") {
        scopedLocalStorage.setItem("updateDataLayer", "true");
        return true;
      }
    }

    return false;
  }
}

function checkUserAssistantInteraction(messages: ChatMessage[]) {
  let userMessageFound = false;
  let assistantOrAgentCount = 0;

  for (let i = 0; i < messages.length; i++) {
    const { role } = messages[i];

    if (role === "user") {
      userMessageFound = true;
    } else if (userMessageFound && (role === "assistant" || role === "agent")) {
      assistantOrAgentCount++;
    }
  }
  if (userMessageFound && assistantOrAgentCount === 1) {
    return true;
  }
  return false;
}

export function toggleShowInput(messages: ChatMessage[]) {
  let userMessageFound = false;
  let assistantOrAgentCount = 0;

  for (let i = 0; i < messages.length; i++) {
    const { role } = messages[i];

    if (role === "user") {
      userMessageFound = true;
    } else if (userMessageFound && (role === "assistant" || role === "agent")) {
      assistantOrAgentCount++;
    }
  }
  if (userMessageFound && assistantOrAgentCount >= 1) {
    return true;
  }
  return false;
}

const updateUtmDetails = async () => {
  let currentUserID = scopedLocalStorage.getItem("user_id");
  const trackingInfo = tracking_info?.get();
  if (currentUserID) {
    try {
      const {
        utm_source,
        utm_medium,
        utm_campaign,
        utm_term,
        utm_content,
        gclid,
        fbclid,
        msclkid,
      } = trackingInfo;
      const trackingInfoPayload = {
        utm_source,
        utm_medium,
        utm_campaign,
        utm_term,
        utm_content,
        gcl_id: gclid,
        fbclid,
        msclkid,
      };
      await axios.post(
        `${BASE_URL}/fe/tracking/${client_id.get()}`,
        { user_id: currentUserID, tracking_info: trackingInfoPayload },
        {
          // headers: { "ngrok-skip-browser-warning": "skip-browser-warning" },
        }
      );
    } catch {
      console.error("Failed to update utm sources");
    }
  }
  return currentUserID;
};

export const displayMessage = (message: string, kind: string = "chat") => {
  let userNewMessage = {
    message: message,
    role: "user",
    timestamp: new Date().toISOString(),
    kind: kind,
    language: scopedLocalStorage?.getItem("lang") || "en",
    is_bot_detected: is_bot_detected?.get() || false,
  };

  let currentMessages = [...(messages?.get() || [])];
  let propertyName = property_name?.get();
  let cityName = city_name?.get();
  let roomType = room_type?.get();
  let messagesToSet = [];

  const isUserMessageFound = currentMessages?.find(
    (message) => message?.role === "user"
  );

  if (
    chatMessages?.get()?.length === 0 &&
    currentMessages?.length <= 2 &&
    currentMessages[0]?.role === "assistant" &&
    !isUserMessageFound
  ) {
    messagesToSet.push({
      message: currentMessages[0]?.message,
      role: "assistant",
      timestamp: new Date().toISOString(),
      language: scopedLocalStorage.getItem("lang") || "en",
      translated_message: currentMessages[0]?.translated_message || "",
      is_bot_detected: is_bot_detected?.get() || false,
    });
  }

  if (
    chatMessages.get().length === 0 &&
    currentMessages.length === 2 &&
    currentMessages[1]?.role === "assistant" &&
    cityName &&
    !propertyName
  ) {
    messagesToSet.push({
      message: currentMessages[1]?.message,
      role: "assistant",
      timestamp: new Date().toISOString(),
      language: scopedLocalStorage.getItem("lang") || "en",
      translated_message: currentMessages[1]?.translated_message || "",
      is_bot_detected: is_bot_detected?.get() || false,
    });
  }

  if (
    chatMessages.get().length === 0 &&
    currentMessages.length === 2 &&
    propertyName &&
    currentMessages[1]?.role === "assistant"
  ) {
    messagesToSet.push({
      message: currentMessages[1]?.message,
      role: "assistant",
      timestamp: new Date().toISOString(),
      language: scopedLocalStorage.getItem("lang") || "en",
      translated_message: currentMessages[1]?.translated_message || "",
      is_bot_detected: is_bot_detected?.get() || false,
    });
  }

  if (
    chatMessages.get().length === 0 &&
    currentMessages.length === 2 &&
    roomType &&
    currentMessages[1]?.role === "assistant"
  ) {
    messagesToSet.push({
      message: currentMessages[1]?.message,
      role: "assistant",
      timestamp: new Date().toISOString(),
      language: scopedLocalStorage.getItem("lang") || "en",
      translated_message: currentMessages[1]?.translated_message || "",
      is_bot_detected: is_bot_detected?.get() || false,
    });
  }

  if (
    chatMessages.get().length === 0 &&
    currentMessages.length === 2 &&
    currentMessages[1]?.role === "assistant" &&
    !cityName &&
    !propertyName &&
    !roomType
  ) {
    messagesToSet.push({
      message: currentMessages[1]?.message,
      role: "assistant",
      timestamp: new Date().toISOString(),
      language: scopedLocalStorage.getItem("lang") || "en",
      translated_message: currentMessages[1]?.translated_message || "",
      is_bot_detected: is_bot_detected?.get() || false,
    });
  }

  currentMessages.push(userNewMessage);
  messagesToSet.push(userNewMessage);

  if (
    clientsWithPrivacyMessage?.includes(client_id?.get() || "") &&
    ((currentMessages.length === 2 && currentMessages[1]?.role === "user") ||
      (currentMessages.length === 3 && currentMessages[2]?.role === "user"))
  ) {
    const privacyMessageForSet = {
      is_bot_detected: false,
      language: "en",
      message:
        "Your details are only used to personalise your experience and provide the best support. We respect your privacy and keep your information secure.",
      role: "assistant",
      timestamp: new Date().toISOString(),
      translated_message: "",
    };
    const privacyMessageForCurrent = {
      message:
        "Your details are only used to personalise your experience and provide the best support. We respect your privacy and keep your information secure.",
      role: "assistant",
      timestamp: new Date().toISOString(),
      language: scopedLocalStorage.getItem("lang") || "en",
      translated_message: currentMessages[1]?.translated_message || "",
      is_bot_detected: is_bot_detected?.get() || false,
    };

    messagesToSet?.splice(-1, 0, privacyMessageForSet);
    currentMessages?.splice(-1, 0, privacyMessageForCurrent);
  }

  chatMessages.set([...chatMessages.get(), ...messagesToSet]);
  messages.set(currentMessages);
  activeAgentIndex.set(shouldDisplayAgentActive(currentMessages));
  activeAssistantIndex.set(shouldDisplayAssistantActive(currentMessages));
};

export const clearSendMessageInputValue = () => {
  send_message_input_value?.set("");
};

export const setSendMessageInputValue = (value: string) => {
  send_message_input_value?.set(value);
};

export const setLanguage = (language: Language) => {
  const selectedLanguageName = language?.name || "English";
  const selectedLanguageCode = language?.language || "en";
  selected_language?.set({
    name: selectedLanguageName,
    code: selectedLanguageCode,
  });
  scopedLocalStorage?.setItem("lang", selectedLanguageCode);
};

export const getBrowserLanguage = () => {
  const browserLang = navigator?.language;
  const baseLang = browserLang?.split("-")[0]?.toLowerCase();
  return baseLang;
};

export const getCurrentLanguage = (id: string, langConfig: any) => {
  if (langConfig) {
    const stored_lang = scopedLocalStorage?.getItem("lang") || "en";

    if (stored_lang) {
      const current_language_name = findLanguageName(stored_lang, langConfig);
      if (current_language_name) {
        return stored_lang;
      } else {
        return getBrowserLanguage();
      }
    }
  } else {
    return scopedLocalStorage?.getItem("lang") || "en";
  }

  if (id === "housinghand") {
    const currentUrl = window?.location?.href;
    if (regexForHousinghand?.test(currentUrl)) {
      return "zh";
    }
  }

  return getBrowserLanguage();
};

export const getLanguageCodeFromUrl = (
  clientId: string,
  languageConfig: any
) => {
  const parsedUrl = new URL(window?.location?.href);
  const segments = parsedUrl?.pathname
    .split("/")
    .filter((segment) => segment !== "");
  const languageCodeFromUrl = segments[clientLanguageIndexConfig?.[clientId]];

  if (languageCodeFromUrl) {
    const current_language_name = findLanguageName(
      languageCodeFromUrl,
      languageConfig
    );

    if (current_language_name) {
      return languageCodeFromUrl;
    } else {
      return scopedLocalStorage?.getItem("lang") || "en";
    }
  }
};

export const findLanguageName = (languageCode: string, clientConfig: any) => {
  return [
    ...(clientConfig?.languages?.popular_languages || []),
    ...(clientConfig?.languages?.other_languages || []),
  ]?.find((lang) => {
    return lang?.language?.toLowerCase() === languageCode?.toLowerCase();
  })?.name;
};

export const handleStartChatting = () => {
  is_privacy_policy_accepted?.set(true);
  scopedLocalStorage?.setItem("is_privacy_policy_accepted", "true");
};
